import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AuthShell from "./AuthShell";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Log in with your new password.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. Request a new link.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell
        title="Missing reset token"
        subtitle="This reset link isn't complete. Request a new one."
        footer={<Link to="/forgot-password" className="text-dox-orange font-semibold hover:underline">Request a new link</Link>}
      >
        <div className="text-sm text-white/60">Ask Dox for a fresh reset link — the token in your URL is missing.</div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password"
      subtitle="Make it 8+ characters. Dox will remember for you."
      footer={<Link to="/login" data-testid="link-back-login" className="text-dox-orange font-semibold hover:underline">Back to login</Link>}
    >
      {done ? (
        <div data-testid="reset-done" className="rounded-xl bg-emerald-500/10 border border-emerald-400/30 px-5 py-4 text-emerald-200 text-sm">
          Password updated. Redirecting to login…
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <Field label="New password" value={password} onChange={setPassword} testid="reset-new" />
          <Field label="Confirm password" value={confirm} onChange={setConfirm} testid="reset-confirm" />
          <button
            data-testid="reset-submit"
            disabled={loading}
            className="w-full rounded-full bg-dox-orange py-3.5 font-bold text-white hover:bg-dox-orangeHover disabled:opacity-60 transition-all min-h-[52px]"
          >
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">{label}</span>
      <input
        data-testid={testid}
        required
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-dox-orange focus:ring-2 focus:ring-dox-orange/20 transition"
      />
    </label>
  );
}
